import { useEffect, useMemo, useState, memo } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Calendar, IndianRupee, TrendingUp, Wallet, Banknote, Eye, Building2, CreditCard, Landmark, ShieldCheck, Receipt, ArrowRightLeft, RefreshCcw, AlertTriangle, ChevronLeft, ChevronRight, FileSpreadsheet, ClipboardList, Combine, Download } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { SearchableSelect } from "../../components/ui/searchable-select";
import { Badge } from "../../components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";
import { Avatar, AvatarFallback, AvatarImage } from "..//../components/ui/avatar";
import { HeroKPICard } from "./HeroKPICard";
import { formatLakhs, formatLakhsOrCr, cn } from "../../lib/utils";
import apiService from "../../services/apiService";
import {
  format,
  startOfWeek, endOfWeek,
  startOfMonth, endOfMonth,
  startOfYear, endOfYear,
  subWeeks, subMonths, subYears,
} from "date-fns";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "../../components/ui/sheet";
import { Frequency } from "../../types/schema";
import * as XLSX from "xlsx";
import astrafinLogo from "../../assets/logo_two.svg";

// ── Types ──────────────────────────────────────────────────────
interface RevenueMISEntry {
  frequency: Frequency;
  lenderAvatar?: string;
  disbursementDate: string;
  lenderName: string;
  customerName: string;
  clientId: string;
  transactionType: string;
  tenure: number;
  rentalStartDate: string;
  rentalMaturedDate: string;
  baseAmount: number;
  gst: number;
  invoiceTotalValue: number;
  disbursedAmountFromLender: number;
  securityDepositPercent: number;
  securityDepositINR: number;
  paymentToVendor: number;
  paymentPendingToVendor: number;
  lenderProcessingFeesPercent: number;
  lenderProcessingFeesINR: number;
  interimRent: number;
  upfrontRevenue: number;
  ptpm: number;
  baseRental: number;
  gstRental: number;
  tds: number;
  totalRental: number;
  gstOverTenure: number;
  actualGstToBePaid: number;
  refundSDPercent: number;
  refundSDINR: number;
  refundSDDate: string;
  overallToAstrafin: number;
  overallToLender: number;
  remitByAstrafin: number;
  status: "Active" | "Matured" | "Pending Disbursement" | "Closed" | "Approved";
  scheduleId: string;
  processingFeePercent: number;
  processingFee: number;
  numberOfRentalsSold: number;
  rentalScheduleCode: string;
  rentalScheduleNumber: string;
}

interface SummaryEntry {
  clientId: string;
  lenderName: string;
  counterpartyName: string;
  transactionType: string;
  sanctionedLimit: number;
  processingFeesPercent: number;
  processingFees: number;
  partnerPayoutPercent: number;
  partnerPayoutINR: number;
  totalInvoiceValue: number;
  utilizedLimit: number;
  unutilizedLimit: number;
  potentialInvoice: number;
  upfrontRevenue: number;
  totalUpfrontRevenue: number;
}

interface HybridEntry {
  clientId: string;
  customerName: string;
  lendingPartner: string;
  transactionType: string;
  requestedLoanAmount: number;
  tenureMonths: number;
  sanctionedAmount: number;
  disbursedAmount: number;
  disbursementDate: string;
  customerReferralFeePercent: number;
  customerReferralFeeAmount: number;
  lenderCommissionPercent: number;
  lenderCommissionAmount: number;
  partnerRevenueSharePercent: number;
  partnerRevenueShareAmount: number;
  revenueRecognition: number;
  grossRevenue: number;
  lenderAvatar?: string;
}

type DatePreset = "this_week" | "previous_week" | "this_month" | "previous_month" | "this_year" | "previous_year" | "custom" | "all";

interface RevenueMisDashboardSummary {
  totalAssetValue: number;
  totalUpfrontRevenue: number;
  totalMonthlyBilling: number;
  nextMonthBilling: number;
  nextToNextMonthBilling: number;
  normalizationBilling: number;
}

type AssetDataEntry = Record<string, any>;

type RevenueMisDashboardSummaryApiResponse = {
  data?: {
    totalAssetValue?: number | string;
    totalUpfrontRevenue?: number | string;
    totalMonthlyBilling?: number | string;
    nextMonthBilling?: number | string;
    nextToNextMonthBilling?: number | string;
    normalizationBilling?: number | string;
  };
};

type RentalFinancialSummaryApiResponse =
  | {
    revenueData?: RevenueMISEntry[];
    summaryData?: SummaryEntry[];
    hybridData?: HybridEntry[];
    disbursement?: RevenueMISEntry[];
    summary?: SummaryEntry[];
    hybrid?: HybridEntry[];
    data?: any;
  }
  | any;

type NoaCustomersLendersApiResponse = {
  data?: {
    customers?: Array<{ id?: string; customerId?: string; name?: string; customerName?: string } | string>;
    lenders?: Array<{ id?: string; lenderCompanyId?: string | number; name?: string; lenderName?: string } | string>;
  };
};

type RevenueFinancialSummaryTotals = {
  totalBaseAssetValue: number;
  totalAssetGst: number;
  totalAssetValue: number;
  totalDisbursedAmount: number;
  totalSecurityDeposit: number;
  totalProcessingFee: number;
  totalUpfrontRevenue: number;
  totalBaseRental: number;
  totalGst: number;
  totalGstOverTenure: number;
  totalActualGstToBePaid: number;
  totalRefundSecurityDeposit: number;
};

const EMPTY_TABLE_TOTALS: RevenueFinancialSummaryTotals = {
  totalBaseAssetValue: 0,
  totalAssetGst: 0,
  totalAssetValue: 0,
  totalDisbursedAmount: 0,
  totalSecurityDeposit: 0,
  totalProcessingFee: 0,
  totalUpfrontRevenue: 0,
  totalBaseRental: 0,
  totalGst: 0,
  totalGstOverTenure: 0,
  totalActualGstToBePaid: 0,
  totalRefundSecurityDeposit: 0,
};

function buildFinancialSummaryFilterParams(
  selectedCustomer: string,
  selectedLender: string,
  from?: Date,
  to?: Date
) {
  const params = new URLSearchParams();
  if (selectedCustomer !== "all") params.set("customerId", selectedCustomer);
  if (selectedLender !== "all") params.set("lenderId", selectedLender);
  if (from && to) {
    params.set("fromDate", format(from, "yyyy-MM-dd"));
    params.set("toDate", format(to, "yyyy-MM-dd"));
  }
  return params;
}

const toNum = (v: any, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const mapTotalsFromApi = (raw: any): RevenueFinancialSummaryTotals => ({
  totalBaseAssetValue: toNum(raw?.totalBaseAssetValue ?? raw?.total_base_asset_value),
  totalAssetGst: toNum(raw?.totalAssetGst ?? raw?.total_asset_gst),
  totalAssetValue: toNum(raw?.totalAssetValue ?? raw?.total_asset_value),
  totalDisbursedAmount: toNum(raw?.totalDisbursedAmount ?? raw?.total_disbursed_amount),
  totalSecurityDeposit: toNum(raw?.totalSecurityDeposit ?? raw?.total_security_deposit),
  totalProcessingFee: toNum(raw?.totalProcessingFee ?? raw?.total_processing_fee),
  totalUpfrontRevenue: toNum(raw?.totalUpfrontRevenue ?? raw?.total_upfront_revenue),
  totalBaseRental: toNum(raw?.totalBaseRental ?? raw?.total_base_rental),
  totalGst: toNum(raw?.totalGst ?? raw?.total_gst),
  totalGstOverTenure: toNum(raw?.totalGstOverTenure ?? raw?.total_gst_over_tenure),
  totalActualGstToBePaid: toNum(raw?.totalActualGstToBePaid ?? raw?.total_actual_gst_to_be_paid),
  totalRefundSecurityDeposit: toNum(
    raw?.totalRefundSecurityDeposit ?? raw?.total_refund_security_deposit
  ),
});

const toStr = (v: any, fallback = "") => (v === null || v === undefined ? fallback : String(v));

const getUnsoldRentalRevenue = (e: RevenueMISEntry) =>
  e.baseRental * Math.max(0, e.tenure - e.numberOfRentalsSold);

/** Matches table row: GST over Tenure − asset GST */
const getActualGstToBePaid = (e: RevenueMISEntry) => e.gstOverTenure - e.gst;

const csvCell = (value: string | number) => {
  const s = String(value ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCsv = (filename: string, headers: string[], rows: (string | number)[][]) => {
  const csvContent = [headers, ...rows].map((row) => row.map(csvCell).join(",")).join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const mapRevenueEntry = (r: any): RevenueMISEntry => ({
  frequency: (r?.frequency ?? "Monthly") as Frequency,
  lenderAvatar: r?.lenderAvatar ?? r?.lender_avatar ?? "",
  disbursementDate: toStr(r?.disbursementDate ?? r?.disbursement_date),
  lenderName: toStr(r?.lenderName ?? r?.lender_name ?? "—"),
  customerName: toStr(r?.customerName ?? r?.customer_name ?? r?.counterpartyName ?? "—"),
  clientId: toStr(r?.clientId ?? r?.client_id),
  transactionType: toStr(r?.transactionType ?? r?.transaction_type ?? "Lease"),
  tenure: toNum(r?.tenure ?? r?.tenureMonths ?? r?.tenure_months),
  rentalStartDate: toStr(r?.rentalStartDate ?? r?.rental_start_date),
  rentalMaturedDate: toStr(r?.rentalMaturedDate ?? r?.rental_matured_date ?? r?.buy_back_date),
  baseAmount: toNum(r?.baseAmount ?? r?.base_amount),
  gst: toNum(r?.gst),
  invoiceTotalValue: toNum(r?.invoiceTotalValue),
  disbursedAmountFromLender: toNum(r?.disbursedAmountFromLender ?? r?.disbursed_amount_from_lender ?? r?.disbursedAmount),
  securityDepositPercent: toNum(r?.securityDepositPercent ?? r?.security_deposit_percent),
  securityDepositINR: toNum(r?.securityDepositINR ?? r?.security_deposit_inr),
  paymentToVendor: toNum(r?.paymentToVendor ?? r?.payment_to_vendor),
  paymentPendingToVendor: toNum(r?.paymentPendingToVendor ?? r?.payment_pending_to_vendor),
  lenderProcessingFeesPercent: toNum(r?.lenderProcessingFeesPercent ?? r?.lender_processing_fees_percent ?? r?.processingFeePercent),
  lenderProcessingFeesINR: toNum(r?.lenderProcessingFeesINR ?? r?.lender_processing_fees_inr ?? r?.processingFee),
  interimRent: toNum(r?.interimRent ?? r?.interim_rent),
  upfrontRevenue: toNum(r?.upfrontRevenue ?? r?.upfront_revenue),
  ptpm: toNum(r?.ptpm),
  baseRental: toNum(r?.baseRental ?? r?.base_rental),
  gstRental: toNum(r?.gstRental ?? r?.gst_rental),
  tds: toNum(r?.tds),
  totalRental: toNum(r?.totalRental ?? r?.total_rental),
  gstOverTenure: toNum(r?.gstOverTenure ?? r?.gst_over_tenure),
  actualGstToBePaid: toNum(r?.actualGstToBePaid ?? r?.actual_gst_to_be_paid),
  refundSDPercent: toNum(r?.refundSDPercent ?? r?.refund_sd_percent),
  refundSDINR: toNum(r?.refundSDINR ?? r?.refund_sd_inr),
  refundSDDate: toStr(r?.refundSDDate ?? r?.refund_sd_date),
  overallToAstrafin: toNum(r?.overallToAstrafin ?? r?.overall_to_astrafin),
  overallToLender: toNum(r?.overallToLender ?? r?.overall_to_lender),
  remitByAstrafin: toNum(r?.remitByAstrafin ?? r?.remit_by_astrafin),
  status: (r?.status ?? "Active") as RevenueMISEntry["status"],
  scheduleId: toStr(r?.scheduleId ?? r?.schedule_id),
  processingFeePercent: toNum(r?.processingFeePercent ?? r?.processing_fee_percent),
  processingFee: toNum(r?.processingFee ?? r?.processing_fee),
  numberOfRentalsSold: toNum(r?.numberOfRentalsSold ?? r?.number_of_rentals_sold),
  rentalScheduleCode: toStr(r?.rentalScheduleCode ?? r?.rental_schedule_code),
  rentalScheduleNumber: toStr(r?.rentalScheduleNumber ?? r?.rental_schedule_number),
});

const mapSummaryEntry = (s: any): SummaryEntry => ({
  clientId: toStr(s?.clientId ?? s?.client_id),
  lenderName: toStr(s?.lenderName ?? s?.lender_name ?? "—"),
  counterpartyName: toStr(s?.counterpartyName ?? s?.counterparty_name ?? s?.customerName ?? "—"),
  transactionType: toStr(s?.transactionType ?? s?.transaction_type ?? "Lease"),
  sanctionedLimit: toNum(s?.sanctionedLimit ?? s?.sanctioned_limit),
  processingFeesPercent: toNum(s?.processingFeesPercent ?? s?.processing_fees_percent),
  processingFees: toNum(s?.processingFees ?? s?.processing_fees),
  partnerPayoutPercent: toNum(s?.partnerPayoutPercent ?? s?.partner_payout_percent),
  partnerPayoutINR: toNum(s?.partnerPayoutINR ?? s?.partner_payout_inr),
  totalInvoiceValue: toNum(s?.totalInvoiceValue ?? s?.total_invoice_value),
  utilizedLimit: toNum(s?.utilizedLimit ?? s?.utilized_limit),
  unutilizedLimit: toNum(s?.unutilizedLimit ?? s?.unutilized_limit),
  potentialInvoice: toNum(s?.potentialInvoice ?? s?.potential_invoice),
  upfrontRevenue: toNum(s?.upfrontRevenue ?? s?.upfront_revenue),
  totalUpfrontRevenue: toNum(s?.totalUpfrontRevenue ?? s?.total_upfront_revenue),
});

const mapHybridEntry = (h: any): HybridEntry => ({
  clientId: toStr(h?.clientId ?? h?.client_id),
  customerName: toStr(h?.customerName ?? h?.customer_name ?? "—"),
  lendingPartner: toStr(h?.lendingPartner ?? h?.lenderName ?? h?.lender_name ?? "—"),
  transactionType: toStr(h?.transactionType ?? h?.transaction_type ?? "Lease"),
  requestedLoanAmount: toNum(h?.requestedLoanAmount ?? h?.requested_loan_amount),
  tenureMonths: toNum(h?.tenureMonths ?? h?.tenure_months),
  sanctionedAmount: toNum(h?.sanctionedAmount ?? h?.sanctioned_amount),
  disbursedAmount: toNum(h?.disbursedAmount ?? h?.disbursed_amount),
  disbursementDate: toStr(h?.disbursementDate ?? h?.disbursement_date),
  customerReferralFeePercent: toNum(h?.customerReferralFeePercent ?? h?.customer_referral_fee_percent),
  customerReferralFeeAmount: toNum(h?.customerReferralFeeAmount ?? h?.customer_referral_fee_amount),
  lenderCommissionPercent: toNum(h?.lenderCommissionPercent ?? h?.lender_commission_percent),
  lenderCommissionAmount: toNum(h?.lenderCommissionAmount ?? h?.lender_commission_amount),
  partnerRevenueSharePercent: toNum(h?.partnerRevenueSharePercent ?? h?.partner_revenue_share_percent),
  partnerRevenueShareAmount: toNum(h?.partnerRevenueShareAmount ?? h?.partner_revenue_share_amount),
  revenueRecognition: toNum(h?.revenueRecognition ?? h?.revenue_recognition),
  grossRevenue: toNum(h?.grossRevenue ?? h?.gross_revenue),
  lenderAvatar: toStr(h?.lenderAvatar ?? h?.lender_avatar),
});

const extractAssetDataRows = (resp: any): AssetDataEntry[] => {
  const root = resp?.data ?? resp;
  if (Array.isArray(root)) return root;
  if (Array.isArray(root?.data)) return root.data;
  if (Array.isArray(root?.assetData)) return root.assetData;
  if (Array.isArray(root?.assetLines)) return root.assetLines;
  if (Array.isArray(root?.rows)) return root.rows;
  if (Array.isArray(root?.entries)) return root.entries;
  return [];
};

// ── Lender helpers ─────────────────────────────────────────────
const getLenderColor = (name: string) => {
  const colors = [
    "from-blue-500 to-blue-700",
    "from-emerald-500 to-emerald-700",
    "from-violet-500 to-violet-700",
    "from-amber-500 to-amber-700",
    "from-rose-500 to-rose-700",
    "from-cyan-500 to-cyan-700",
    "from-indigo-500 to-indigo-700",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

const getLenderInitials = (name: string) => {
  if (!name || name === "—") return "—";
  return name.split(" ").map(w => w[0]).join("").substring(0, 2).toUpperCase();
};

const getLenderLogoUrl = (name: string): string | undefined => {
  // Add/adjust as you have real logo URLs/paths available
  const key = String(name || "").trim().toLowerCase();
  const map: Record<string, string> = {
  };
  return map[key];
};

const isMissingLenderName = (name?: string): boolean => {
  const n = String(name ?? "").trim();
  return !n || n === "—" || n === "-";
};

const toAbsoluteLenderAvatarUrl = (avatarPath?: string): string | undefined => {
  if (!avatarPath) return undefined;
  if (/^https?:\/\//i.test(avatarPath)) return avatarPath;

  const baseUrl = String(import.meta.env.VITE_API_BASE_URL || "").replace(/\/+$/, "");
  const cleanPath = String(avatarPath).replace(/^\/+/, "");
  if (!baseUrl) return `/${cleanPath}`;
  return `${baseUrl}/${cleanPath}`;
};

const LenderAvatar = memo(function LenderAvatar({ name, logoUrl }: { name: string; logoUrl?: string }) {
  const resolvedLogoUrl = logoUrl || getLenderLogoUrl(name);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
  }, [resolvedLogoUrl]);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-2 min-w-0">
            <Avatar className="h-8 w-15 shrink-0">
              {resolvedLogoUrl ? (
                <AvatarImage
                  src={resolvedLogoUrl}
                  className={`w-15 h-8 ${loaded ? "opacity-100" : "opacity-0"} transition-opacity duration-150`}
                  alt={name}
                  onLoad={() => setLoaded(true)}
                  onError={() => setLoaded(false)}
                />
              ) : null}
              {(!resolvedLogoUrl || !loaded) && (
                <AvatarFallback className={`bg-gradient-to-br ${getLenderColor(name)} text-white text-[9px] font-bold`}>
                  {getLenderInitials(name)}
                </AvatarFallback>
              )}
            </Avatar>
            <span className="font-medium truncate max-w-[140px]">{name}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent>{name}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
});

// ── Status helpers ─────────────────────────────────────────────
const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline"; className: string }> = {
  Active: { label: "Active", variant: "default", className: "bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100" },
  Matured: { label: "Matured", variant: "destructive", className: "bg-red-100 text-red-700 border-red-200 hover:bg-red-100" },
  "Pending Disbursement": { label: "Pending", variant: "outline", className: "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-50" },
  Closed: { label: "Closed", variant: "secondary", className: "bg-muted text-muted-foreground border-border hover:bg-muted" },
};

const StatusBadge = ({ status }: { status: string }) => {
  const cfg = statusConfig[status] || statusConfig.Closed;
  return <Badge variant={cfg.variant} className={`text-[10px] px-2 py-0.5 font-medium ${cfg.className}`}>{cfg.label}</Badge>;
};

const getActiveOrMaturedStatus = (entry: Partial<RevenueMISEntry> & Record<string, any>): "Active" | "Matured" => {
  const buyBackDate =
    entry?.rentalMaturedDate;
  if (!buyBackDate) return "Active";
  const parsed = new Date(buyBackDate);
  if (Number.isNaN(parsed.getTime())) return "Active";

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  parsed.setHours(0, 0, 0, 0);

  return parsed < today ? "Matured" : "Active";
};

const upfrontRevenueToneClass = (n: number) =>
  n < 0 ? "text-red-600" : n > 0 ? "text-emerald-600" : "";

// ── Detail row with icon ──
const DetailRow = ({
  icon,
  label,
  value,
  highlight,
  valueClassName,
}: {
  icon?: React.ReactNode;
  label: string;
  value: string;
  highlight?: boolean;
  valueClassName?: string;
}) => (
  <div className="flex justify-between items-center border-b pb-2">
    <div className="flex items-center gap-2">
      {icon && <span className="text-muted-foreground">{icon}</span>}
      <span className="text-sm text-muted-foreground">{label}</span>
    </div>
    <span
      className={cn(
        "text-sm font-medium",
        valueClassName ?? (highlight ? "text-amber-600" : ""),
      )}
    >
      {value}
    </span>
  </div>
);

// ── Sticky column widths (fixed pixel widths to prevent overlap) ──
const COL1_W = "min-w-[131px] w-[131px] max-w-[131px]";
const COL2_W = "min-w-[151px] w-[151px] max-w-[151px]";
const COL3_W = "min-w-[161px] w-[161px] max-w-[161px]";
const COL1_LEFT = "left-0";
const COL2_LEFT = "left-[130px]";
const COL3_LEFT = "left-[280px]";

// ── Pagination Bar ──
const PaginationBar = ({ current, total, count, onPage }: { current: number; total: number; count: number; onPage: (p: number) => void }) => {
  const from = (current - 1) * ITEMS_PER_PAGE + 1;
  const to = Math.min(current * ITEMS_PER_PAGE, count);
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t">
      <span className="text-xs text-muted-foreground">
        Showing {from}–{to} of {count} records (Total computed on all {count})
      </span>
      <div className="flex items-center gap-1">
        <Button variant="outline" size="sm" disabled={current <= 1} onClick={() => onPage(current - 1)} className="h-8 w-8 p-0">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        {Array.from({ length: total }, (_, i) => i + 1)
          .filter(p => p === 1 || p === total || Math.abs(p - current) <= 1)
          .reduce<(number | "...")[]>((acc, p, idx, arr) => {
            if (idx > 0 && p - (arr[idx - 1]) > 1) acc.push("...");
            acc.push(p);
            return acc;
          }, [])
          .map((p, i) =>
            p === "..." ? (
              <span key={`e${i}`} className="px-1 text-xs text-muted-foreground">…</span>
            ) : (
              <Button key={p} variant={p === current ? "default" : "outline"} size="sm" onClick={() => onPage(p as number)} className="h-8 w-8 p-0 text-xs">
                {p}
              </Button>
            )
          )}
        <Button variant="outline" size="sm" disabled={current >= total} onClick={() => onPage(current + 1)} className="h-8 w-8 p-0">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────
const ITEMS_PER_PAGE = 50;

const RevenueMIS = () => {
  const [selectedCustomer, setSelectedCustomer] = useState<string>("all");
  const [selectedLender, setSelectedLender] = useState<string>("all");
  const [activeTab, setActiveTab] = useState<string>("disbursement");
  const [datePreset, setDatePreset] = useState<DatePreset>("all");
  const [customDateRange, setCustomDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({ from: undefined, to: undefined });
  const [viewEntry, setViewEntry] = useState<RevenueMISEntry | null>(null);
  const [viewSummaryEntry, setViewSummaryEntry] = useState<SummaryEntry | null>(null);
  const [viewHybridEntry, setViewHybridEntry] = useState<HybridEntry | null>(null);
  const [disbursementPage, setDisbursementPage] = useState(1);
  const [summaryPage, setSummaryPage] = useState(1);
  const [hybridPage, setHybridPage] = useState(1);
  const [revenueData, setRevenueData] = useState<RevenueMISEntry[]>([]);
  const [summaryData, setSummaryData] = useState<SummaryEntry[]>([]);
  const [hybridData, setHybridData] = useState<HybridEntry[]>([]);
  const [disbursementTotalCount, setDisbursementTotalCount] = useState<number>(0);
  const [summaryTotalCount, setSummaryTotalCount] = useState<number>(0);
  const [hybridTotalCount, setHybridTotalCount] = useState<number>(0);
  const [disbursementTotalPages, setDisbursementTotalPages] = useState<number>(0);
  const [summaryTotalPages, setSummaryTotalPages] = useState<number>(0);
  const [hybridTotalPages, setHybridTotalPages] = useState<number>(0);
  const [apiCustomers, setApiCustomers] = useState<Array<{ id: string; name: string }>>([]);
  const [apiLenders, setApiLenders] = useState<Array<{ id: string; name: string }>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dashboardSummary, setDashboardSummary] = useState<RevenueMisDashboardSummary>({
    totalAssetValue: 0,
    totalUpfrontRevenue: 0,
    totalMonthlyBilling: 0,
    nextMonthBilling: 0,
    nextToNextMonthBilling: 0,
    normalizationBilling: 0,
  });
  const [isDashboardSummaryLoading, setIsDashboardSummaryLoading] = useState(false);
  const [tableTotals, setTableTotals] = useState<RevenueFinancialSummaryTotals>(EMPTY_TABLE_TOTALS);
  const [isTableTotalsLoading, setIsTableTotalsLoading] = useState(false);
  const [assetData, setAssetData] = useState<AssetDataEntry[]>([]);
  const [hybridDataExport, setHybridDataExport] = useState<HybridEntry[]>([]);
  const [revenueDataExport, setRevenueDataExport] = useState<RevenueMISEntry[]>([]);
  const [assetDataExport, setAssetDataExport] = useState<AssetDataEntry[]>([]);
  const [summaryDataExport, setSummaryDataExport] = useState<SummaryEntry[]>([]);
  

  // ── Date range logic ──
  const getDateRange = useMemo(() => {
    const today = new Date();
    switch (datePreset) {
      case "this_week": return { from: startOfWeek(today, { weekStartsOn: 1 }), to: endOfWeek(today, { weekStartsOn: 1 }) };
      case "previous_week": { const s = subWeeks(startOfWeek(today, { weekStartsOn: 1 }), 1); return { from: s, to: endOfWeek(s, { weekStartsOn: 1 }) }; }
      case "this_month": return { from: startOfMonth(today), to: endOfMonth(today) };
      case "previous_month": { const s = subMonths(startOfMonth(today), 1); return { from: s, to: endOfMonth(s) }; }
      case "this_year": return { from: startOfYear(today), to: endOfYear(today) };
      case "previous_year": { const s = subYears(startOfYear(today), 1); return { from: s, to: endOfYear(s) }; }
      case "custom": return customDateRange;
      default: return { from: undefined, to: undefined };
    }
  }, [datePreset, customDateRange]);

  // ── Fetch financial summary (API) ──
  useEffect(() => {
    let ignore = false;

    const fetchFinancialSummary = async () => {
      setIsLoading(true);
      try {
        const page =
          activeTab === "summary"
            ? summaryPage
            : activeTab === "hybrid"
              ? hybridPage
              : disbursementPage;

        const params = buildFinancialSummaryFilterParams(
          selectedCustomer,
          selectedLender,
          getDateRange.from,
          getDateRange.to
        );
        params.set("page", String(page));
        params.set("pageSize", String(ITEMS_PER_PAGE));
        const endpoint =
          activeTab === "hybrid"
            ? "/rental-schedule/hybrid-revenue-entries"
            : "/rental-schedule/rental-financial-summary";
        const asset_url = "/rental-schedule/get-approved-asset-lines" + (params.toString() ? `?${params.toString()}` : "");
        const asset_resp = await apiService.get<any>(asset_url);
        const nextAssetData = extractAssetDataRows(asset_resp);
        const url = `${endpoint}${params.toString() ? `?${params.toString()}` : ""}`;
        const resp = await apiService.get<RentalFinancialSummaryApiResponse>(url);
        const root: any = resp;
        const payload: any = root?.data ?? root;
        const isHybridTab = activeTab === "hybrid";

        const rawRevenue = isHybridTab
          ? []
          : (
            (Array.isArray(payload?.revenueData) && payload.revenueData) ||
            (Array.isArray(payload?.disbursement) && payload.disbursement) ||
            (Array.isArray(payload?.revenue) && payload.revenue) ||
            (Array.isArray(payload) && payload) ||
            []
          );

        const rawSummary = isHybridTab
          ? []
          : (
            (Array.isArray(payload?.summaryData) && payload.summaryData) ||
            (Array.isArray(payload?.summary) && payload.summary) ||
            []
          );

        const rawHybrid = isHybridTab
          ? (
            (Array.isArray(payload?.hybridData) && payload.hybridData) ||
            (Array.isArray(payload?.hybrid) && payload.hybrid) ||
            (Array.isArray(payload?.entries) && payload.entries) ||
            (Array.isArray(payload) && payload) ||
            []
          )
          : [];
        const nextRevenue: RevenueMISEntry[] = rawRevenue.map(mapRevenueEntry);
        const nextSummary: SummaryEntry[] = rawSummary.map(mapSummaryEntry);
        const nextHybrid: HybridEntry[] = rawHybrid.map(mapHybridEntry);

        const pagination: any = root?.pagination ?? payload?.pagination ?? null;
        const nextTotalRecords = Number(pagination?.totalRecords);
        const nextTotalPages = Number(pagination?.totalPages);
        const totalRecords = Number.isFinite(nextTotalRecords) && nextTotalRecords >= 0 ? nextTotalRecords : 0;
        const totalPages = Number.isFinite(nextTotalPages) && nextTotalPages >= 0 ? nextTotalPages : 0;

        if (ignore) return;
        setRevenueData(nextRevenue);
        setSummaryData(nextSummary);
        setHybridData(nextHybrid);
        setAssetData(nextAssetData);

        // Pagination returned is for the requested (active) tab/page
        if (activeTab === "summary") {
          setSummaryTotalCount(totalRecords);
          setSummaryTotalPages(totalPages);
        } else if (activeTab === "hybrid") {
          setHybridTotalCount(totalRecords);
          setHybridTotalPages(totalPages);
        } else {
          setDisbursementTotalCount(totalRecords);
          setDisbursementTotalPages(totalPages);
        }
      } catch (e) {
        if (ignore) return;
        setRevenueData([]);
        setSummaryData([]);
        setHybridData([]);
        setAssetData([]);
        setDisbursementTotalCount(0);
        setSummaryTotalCount(0);
        setHybridTotalCount(0);
        setDisbursementTotalPages(0);
        setSummaryTotalPages(0);
        setHybridTotalPages(0);
      } finally {
        if (!ignore) setIsLoading(false);
      }
    };

    fetchFinancialSummary();
    return () => { ignore = true; };
  }, [activeTab, disbursementPage, summaryPage, hybridPage, selectedCustomer, selectedLender, getDateRange.from, getDateRange.to]);

  // ── Fetch disbursement table totals (all filtered records) ──
  useEffect(() => {
    if (activeTab !== "disbursement") return;
    let ignore = false;

    const fetchTableTotals = async () => {
      setIsTableTotalsLoading(true);
      try {
        const params = buildFinancialSummaryFilterParams(
          selectedCustomer,
          selectedLender,
          getDateRange.from,
          getDateRange.to
        );
        const qs = params.toString();
        const url = `/rental-schedule/rental-financial-summary-totals${qs ? `?${qs}` : ""}`;
        const resp = await apiService.get(url);
        const root: any = resp;
        const payload: any = root?.data ?? root;
        if (!ignore) setTableTotals(mapTotalsFromApi(payload));
      } catch {
        if (!ignore) setTableTotals(EMPTY_TABLE_TOTALS);
      } finally {
        if (!ignore) setIsTableTotalsLoading(false);
      }
    };

    fetchTableTotals();
    return () => { ignore = true; };
  }, [activeTab, selectedCustomer, selectedLender, getDateRange.from, getDateRange.to]);

  // ── Filters source (API) ──
  useEffect(() => {
    let ignore = false;

    const fetchNoaCustomersAndLenders = async () => {
      try {
        const resp = await apiService.get<NoaCustomersLendersApiResponse>("/rental-schedule/noa-customers-and-lenders");
        const payload = (resp as any)?.data ?? {};

        const customers = Array.isArray(payload?.customers) ? payload.customers : [];
        const lenders = Array.isArray(payload?.lenders) ? payload.lenders : [];

        const parsedCustomers = customers
          .map((c: any) => {
            if (typeof c === "string") return { id: c, name: c };
            const id = String(c?.id ?? c?.customerId ?? "").trim();
            const name = String(c?.name ?? c?.customerName ?? "").trim();
            if (!id || !name) return null;
            return { id, name };
          })
          .filter(Boolean) as Array<{ id: string; name: string }>;

        const parsedLenders = lenders
          .map((l: any) => {
            if (typeof l === "string") return { id: l, name: l };
            const id = String(l?.id ?? l?.lenderCompanyId ?? "").trim();
            const name = String(l?.name ?? l?.lenderName ?? "").trim();
            if (!id || !name) return null;
            return { id, name };
          })
          .filter(Boolean) as Array<{ id: string; name: string }>;

        if (ignore) return;
        setApiCustomers(parsedCustomers);
        setApiLenders(parsedLenders);
      } catch {
        if (ignore) return;
        setApiCustomers([]);
        setApiLenders([]);
      }
    };

    fetchNoaCustomersAndLenders();
    return () => { ignore = true; };
  }, []);

  // ── Dashboard KPI summary (API) ──
  useEffect(() => {
    let ignore = false;

    const fetchDashboardSummary = async () => {
      setIsDashboardSummaryLoading(true);
      try {
        const resp = await apiService.get<RevenueMisDashboardSummaryApiResponse>(
          "/rental-schedule/revenue-mis-dashboard-summary"
        );
        const r = resp?.data ?? {};
        if (ignore) return;
        setDashboardSummary({
          totalAssetValue: Number(r.totalAssetValue ?? 0),
          totalUpfrontRevenue: Number(r.totalUpfrontRevenue ?? 0),
          totalMonthlyBilling: Number(r.totalMonthlyBilling ?? 0),
          nextMonthBilling: Number(r.nextMonthBilling ?? 0),
          nextToNextMonthBilling: Number(r.nextToNextMonthBilling ?? 0),
          normalizationBilling: Number(r.normalizationBilling ?? 0),
        });
      } catch {
        if (ignore) return;
        setDashboardSummary({
          totalAssetValue: 0,
          totalUpfrontRevenue: 0,
          totalMonthlyBilling: 0,
          nextMonthBilling: 0,
          nextToNextMonthBilling: 0,
          normalizationBilling: 0,
        });
      } finally {
        if (!ignore) setIsDashboardSummaryLoading(false);
      }
    };

    fetchDashboardSummary();
    return () => { ignore = true; };
  }, []);

  const kpiAumDisplay = isDashboardSummaryLoading
    ? "—"
    : formatLakhsOrCr(dashboardSummary.totalAssetValue);
  const kpiRevenueDisplay = isDashboardSummaryLoading
    ? "—"
    : formatLakhsOrCr(dashboardSummary.totalUpfrontRevenue);

  const fmtTotal = (n: number) => (isTableTotalsLoading ? "—" : fmt(n));

  const summaryTotals = useMemo(() => summaryData.reduce((t, e) => ({
    sanctionedLimit: t.sanctionedLimit + e.sanctionedLimit,
    processingFees: t.processingFees + e.processingFees,
    partnerPayoutINR: t.partnerPayoutINR + e.partnerPayoutINR,
    totalInvoiceValue: t.totalInvoiceValue + e.totalInvoiceValue,
    utilizedLimit: t.utilizedLimit + e.utilizedLimit,
    unutilizedLimit: t.unutilizedLimit + e.unutilizedLimit,
    potentialInvoice: t.potentialInvoice + e.potentialInvoice,
    upfrontRevenue: t.upfrontRevenue + e.upfrontRevenue,
    totalUpfrontRevenue: t.totalUpfrontRevenue + e.totalUpfrontRevenue,
  }), {
    sanctionedLimit: 0, processingFees: 0, partnerPayoutINR: 0, totalInvoiceValue: 0,
    utilizedLimit: 0, unutilizedLimit: 0, potentialInvoice: 0, upfrontRevenue: 0, totalUpfrontRevenue: 0,
  }), [summaryData]);

  const hybridTotals = useMemo(() => hybridData.reduce((t, e) => ({
    requestedLoanAmount: t.requestedLoanAmount + e.requestedLoanAmount,
    sanctionedAmount: t.sanctionedAmount + e.sanctionedAmount,
    disbursedAmount: t.disbursedAmount + e.disbursedAmount,
    customerReferralFeeAmount: t.customerReferralFeeAmount + e.customerReferralFeeAmount,
    lenderCommissionAmount: t.lenderCommissionAmount + e.lenderCommissionAmount,
    partnerRevenueShareAmount: t.partnerRevenueShareAmount + e.partnerRevenueShareAmount,
    revenueRecognition: t.revenueRecognition + e.revenueRecognition,
    grossRevenue: t.grossRevenue + e.grossRevenue,
  }), {
    requestedLoanAmount: 0, sanctionedAmount: 0, disbursedAmount: 0,
    customerReferralFeeAmount: 0, lenderCommissionAmount: 0, partnerRevenueShareAmount: 0,
    revenueRecognition: 0, grossRevenue: 0,
  }), [hybridData]);

  // ── Helpers ──
  const customerOptions = useMemo(() => [
    { value: "all", label: "All Customers" },
    ...apiCustomers.map(c => ({ value: c.id, label: c.name })),
  ], [apiCustomers]);

  const lenderOptions = useMemo(() => [
    { value: "all", label: "All Lenders" },
    ...apiLenders.map(l => ({ value: l.id, label: l.name })),
  ], [apiLenders]);

  const fmt = (v: number) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Math.round(v));
  const formatTenure = (e: RevenueMISEntry) =>
    e.frequency === "monthly" ? `${e.tenure}M` : `${e.tenure / 3}Q`;
  const formatRentalsSold = (e: RevenueMISEntry) =>
    e.frequency === "monthly" ? `${e.numberOfRentalsSold}M` : `${e.numberOfRentalsSold}Q`;
  const activeTabCount =
    activeTab === "hybrid" ? hybridTotalCount : disbursementTotalCount;
  const showExportButton = activeTabCount > 0;

  const handleExport = async () => {

    const params = buildFinancialSummaryFilterParams(
      selectedCustomer,
      selectedLender,
      getDateRange.from,
      getDateRange.to
    );

    const endpoint =
      activeTab === "hybrid"
        ? "/rental-schedule/hybrid-revenue-entries-export"
        : "/rental-schedule/rental-financial-summary-export";
    const asset_url = "/rental-schedule/get-approved-asset-lines" + (params.toString() ? `?${params.toString()}` : "");
    const asset_resp = await apiService.get<any>(asset_url);
    const assetDataExport = extractAssetDataRows(asset_resp);
    const url = `${endpoint}${params.toString() ? `?${params.toString()}` : ""}`;
    const resp = await apiService.get<RentalFinancialSummaryApiResponse>(url);
    const root: any = resp;
    const payload: any = root?.data ?? root;
    const isHybridTab = activeTab === "hybrid";

    const rawRevenue = isHybridTab
      ? []
      : (
        (Array.isArray(payload?.revenueData) && payload.revenueData) ||
        (Array.isArray(payload?.disbursement) && payload.disbursement) ||
        (Array.isArray(payload?.revenue) && payload.revenue) ||
        (Array.isArray(payload) && payload) ||
        []
      );

    const rawSummary = isHybridTab
      ? []
      : (
        (Array.isArray(payload?.summaryData) && payload.summaryData) ||
        (Array.isArray(payload?.summary) && payload.summary) ||
        []
      );

    const rawHybrid = isHybridTab
      ? (
        (Array.isArray(payload?.hybridData) && payload.hybridData) ||
        (Array.isArray(payload?.hybrid) && payload.hybrid) ||
        (Array.isArray(payload?.entries) && payload.entries) ||
        (Array.isArray(payload) && payload) ||
        []
      )
      : [];
    const revenueDataExport: RevenueMISEntry[] = rawRevenue.map(mapRevenueEntry);
    const summaryDataExport: SummaryEntry[] = rawSummary.map(mapSummaryEntry);
    const hybridDataExport: HybridEntry[] = rawHybrid.map(mapHybridEntry);

    setSummaryDataExport(summaryDataExport);
    setHybridDataExport(hybridDataExport);
    setRevenueDataExport(revenueDataExport);
    setAssetDataExport(assetDataExport);

    const dateSuffix = format(new Date(), "yyyy-MM-dd");

    if (activeTab === "hybrid") {
      const headers = [
        "Client ID", "Customer Name", "Lending Partner", "Transaction Type",
        "Requested Loan Amt", "Tenure (Months)", "Sanctioned Amt", "Disbursed Amt",
        "Disbursement Date", "Cust Referral Fee %", "Cust Referral Fee Amt",
        "Lender Comm %", "Lender Comm Amt", "Partner Rev Share %", "Partner Rev Share Amt",
        "Revenue Recognition",
      ];
      const rows = hybridDataExport.map((e) => [
        e.clientId, e.customerName, e.lendingPartner, e.transactionType,
        e.requestedLoanAmount, e.tenureMonths, e.sanctionedAmount, e.disbursedAmount,
        e.disbursementDate, e.customerReferralFeePercent, e.customerReferralFeeAmount,
        e.lenderCommissionPercent, e.lenderCommissionAmount,
        e.partnerRevenueSharePercent, e.partnerRevenueShareAmount, e.revenueRecognition,
      ]);
      downloadCsv(`revenue-mis-hybrid-${dateSuffix}.csv`, headers, rows);
      return;
    }

    const headers = [
      "Disbursement Date", "Lender Name", "Customer Name", "Client ID", "RS-NO",
      "Transaction Type", "Tenure", "Asset Base Amt", "GST", "Total Asset Value",
      "Disbursed Amt", "Lender Proc Fees (INR)", "SD (%)", "SD (INR)",
      "Proc Fees (%)", "Proc Fees (INR)", "Interim Rent", "Upfront Revenue",
      "Unsold Rental Revenue", "PTPM", "Base Rental", "GST Rental", "GST over Tenure",
      "Actual GST to be Paid", "Refund SD (%)", "Refund SD (INR)", "Refund SD Date",
      "Rental Start", "Rental Matured", "To Astrafin", "To Lender", "Remit by Astrafin",
      "Number of Rentals Sold", "Status",
    ];
    const rows = revenueDataExport.map((e) => [
      e.disbursementDate, e.lenderName, e.customerName, e.clientId, e.rentalScheduleNumber,
      e.transactionType, formatTenure(e), e.baseAmount, e.gst, e.invoiceTotalValue,
      e.disbursedAmountFromLender, e.lenderProcessingFeesINR, e.securityDepositPercent,
      e.securityDepositINR, e.processingFeePercent, e.processingFee, e.interimRent,
      e.upfrontRevenue, getUnsoldRentalRevenue(e), e.ptpm, e.baseRental, e.gstRental,
      e.gstOverTenure, getActualGstToBePaid(e), e.refundSDPercent, e.refundSDINR,
      e.refundSDDate, e.rentalStartDate, e.rentalMaturedDate, e.overallToAstrafin,
      e.overallToLender, e.remitByAstrafin, formatRentalsSold(e),
      getActiveOrMaturedStatus(e),
    ]);

    const table_header = [
      "Entity Name", "PO ID", "Invoice NO", "RS-NO", "Item", "Description",
      "HSN", "Base Amount", "Quantity", "GST", "Total"
    ];

    const table_rows = assetDataExport.map((e) => [
      e.entityName, e.poId, e.invoiceId, e.display_id, e.item, e.description,
      e.hsn, e.baseAmount, e.qty, e.taxAmount, e.grandTotal
    ]);

    const workbook = XLSX.utils.book_new();
    const summarySheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

    const assetSheet = XLSX.utils.aoa_to_sheet([table_header, ...table_rows]);
    XLSX.utils.book_append_sheet(workbook, assetSheet, "Asset Details");

    XLSX.writeFile(workbook, `revenue-mis-${dateSuffix}.xlsx`);
  };

  const currency = (n: number) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const lakhsLabel = (n: number) => {
    const value = Number(n || 0);
    const abs = Math.abs(value);
    if (abs >= 10_000_000) {
      return `₹${parseFloat((value / 10_000_000).toFixed(2))}Cr`;
    }
    if (abs >= 100_000) {
      return `₹${parseFloat((value / 100_000).toFixed(2))}L`;
    }
    if (abs >= 1_000) {
      return `₹${parseFloat((value / 1_000).toFixed(2))}K`;
    }
    return currency(value);
  };

  const getDatePresetLabel = () => {
    switch (datePreset) {
      case "this_week": return "This Week";
      case "previous_week": return "Previous Week";
      case "this_month": return "This Month";
      case "previous_month": return "Previous Month";
      case "this_year": return "This Year";
      case "previous_year": return "Previous Year";
      case "custom":
        if (customDateRange.from && customDateRange.to) return `${format(customDateRange.from, "dd MMM")} - ${format(customDateRange.to, "dd MMM yyyy")}`;
        return "Custom Range";
      default: return "All Time";
    }
  };

  // ── View icon with outstanding color ──
  const ViewButton = ({ onClick, hasOutstanding }: { onClick: () => void; hasOutstanding?: boolean }) => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClick}>
            <Eye className={`h-3.5 w-3.5 ${hasOutstanding ? "text-amber-600" : "text-muted-foreground"}`} />
            {hasOutstanding && <AlertTriangle className="h-2.5 w-2.5 text-amber-600 absolute -top-0.5 -right-0.5" />}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{hasOutstanding ? "View Details (Outstanding)" : "View Details"}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  // ── Render ──
  return (
    <div className="space-y-6 w-full max-w-full overflow-x-hidden p-6 bg-white">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <h2 className="text-2xl font-semibold tracking-tight">Revenue MIS</h2>
        <div className="flex flex-wrap gap-3 items-center">
          <SearchableSelect options={customerOptions} value={selectedCustomer} onValueChange={setSelectedCustomer} placeholder="Select customer" searchPlaceholder="Search customers..." emptyMessage="No customers found." className="w-[220px]" />
          <SearchableSelect options={lenderOptions} value={selectedLender} onValueChange={setSelectedLender} placeholder="Select lender" searchPlaceholder="Search lenders..." emptyMessage="No lenders found." className="w-[200px]" />
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-[200px] justify-start gap-2">
                <Calendar className="h-4 w-4" />
                <span className="truncate">{getDatePresetLabel()}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <div className="p-3 space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: "all", label: "All Time" }, { value: "this_week", label: "This Week" },
                    { value: "previous_week", label: "Previous Week" }, { value: "this_month", label: "This Month" },
                    { value: "previous_month", label: "Previous Month" }, { value: "this_year", label: "This Year" },
                    { value: "previous_year", label: "Previous Year" }, { value: "custom", label: "Custom Range" },
                  ].map(p => (
                    <Button key={p.value} variant={datePreset === p.value ? "default" : "outline"} size="sm" onClick={() => setDatePreset(p.value as DatePreset)} className="justify-start">{p.label}</Button>
                  ))}
                </div>
                {datePreset === "custom" && (
                  <div className="border-t pt-3">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div className="space-y-1">
                        <label className="text-xs text-muted-foreground">From Date</label>
                        <input
                          type="date"
                          className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                          value={customDateRange.from ? format(customDateRange.from, "yyyy-MM-dd") : ""}
                          onChange={(e) =>
                            setCustomDateRange((prev) => ({
                              ...prev,
                              from: e.target.value ? new Date(`${e.target.value}T00:00:00`) : undefined,
                            }))
                          }
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs text-muted-foreground">To Date</label>
                        <input
                          type="date"
                          className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                          value={customDateRange.to ? format(customDateRange.to, "yyyy-MM-dd") : ""}
                          onChange={(e) =>
                            setCustomDateRange((prev) => ({
                              ...prev,
                              to: e.target.value ? new Date(`${e.target.value}T23:59:59`) : undefined,
                            }))
                          }
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* KPI Cards */}
      <section>
        <div className="grid grid-cols-6 sm:grid-cols-6 lg:grid-cols-6 gap-4">
          <HeroKPICard title="Total Active Portfolio (AUM)" value={kpiAumDisplay} icon={<IndianRupee className="h-4 w-4" />} variant="default" />
          <HeroKPICard title="Current Month Billing" value={lakhsLabel(dashboardSummary.totalMonthlyBilling)} subtitle="Monthly invoiced" icon={<IndianRupee className="h-4 w-4" />} variant="info" />
          <HeroKPICard title="Next Month Billing" value={lakhsLabel(dashboardSummary.nextMonthBilling)} subtitle="Next month projected" icon={<IndianRupee className="h-4 w-4" />} variant="info" />
          <HeroKPICard title="Following Month Billing" value={lakhsLabel(dashboardSummary.nextToNextMonthBilling)} subtitle="Following month projected" icon={<IndianRupee className="h-4 w-4" />} variant="info" />
          <HeroKPICard title="Normalization Billing" value={lakhsLabel(dashboardSummary.normalizationBilling)} subtitle="Normalization invoiced" icon={<IndianRupee className="h-4 w-4" />} variant="info" />
          <HeroKPICard title="Total Revenue" value={kpiRevenueDisplay} subtitle="Upfront + Interim + SD + Fees" icon={<TrendingUp className="h-4 w-4" />} variant="success" />
          {/* <HeroKPICard title="Vendor Payment Outstanding" value="-" icon={<Wallet className="h-4 w-4" />} variant="default" /> */}
          
        </div>
      </section>

      {/* Table Card */}
      <Card className="w-full max-w-full overflow-hidden">
        <CardContent className="p-4 w-full max-w-full overflow-hidden">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full max-w-full overflow-hidden">
            <div className="mb-4 flex items-center justify-between gap-4">
              <TabsList>
                <TabsTrigger value="disbursement" className="gap-1.5"><FileSpreadsheet className="h-3.5 w-3.5" />Leasing Disbursement</TabsTrigger>
                <TabsTrigger value="hybrid" className="gap-1.5"><Combine className="h-3.5 w-3.5" />Hybrid</TabsTrigger>
              </TabsList>
              {showExportButton && (
                <Button variant="outline" size="sm" onClick={handleExport} className="gap-1.5 shrink-0">
                  <Download className="h-3.5 w-3.5" />
                  Export
                </Button>
              )}
            </div>

            {/* ── Disbursement Tab ── */}
            <TabsContent value="disbursement" className="mt-0 w-full max-w-full overflow-hidden">
              <div className="w-full max-w-full h-[500px] overflow-x-auto overflow-y-auto overscroll-contain border rounded-md">
                <Table className="w-max min-w-full">
                  <TableHeader className="sticky top-0 z-30 bg-muted/95 backdrop-blur-sm">
                    <TableRow>
                      <TableHead className={`sticky ${COL1_LEFT} z-40 bg-muted/95 whitespace-nowrap border-r text-xs ${COL1_W}`}>Disbursement Date</TableHead>
                      <TableHead className={`sticky ${COL2_LEFT} z-40 bg-muted/95 whitespace-nowrap border-r text-xs ${COL2_W}`}>Lender Name</TableHead>
                      <TableHead className={`sticky ${COL3_LEFT} z-40 bg-muted/95 whitespace-nowrap border-r text-xs ${COL3_W}`}>Customer Name</TableHead>
                      <TableHead className="whitespace-nowrap text-xs min-w-[100px]">Client ID</TableHead>
                      <TableHead className="whitespace-nowrap text-xs min-w-[100px]">RS-NO</TableHead>
                      <TableHead className="whitespace-nowrap text-xs min-w-[110px]">Transaction Type</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[70px]">Tenure</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[120px]">Asset Base Amt</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[90px]">GST</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[120px]">Total Asset Value</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[120px]">Disbursed Amt</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[110px]">Lender Proc Fees (₹)</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[70px]">SD (%)</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[100px]">SD (₹)</TableHead>
                      {/* <TableHead className="whitespace-nowrap text-right text-xs min-w-[130px]">Payment to Vendor</TableHead> */}
                      {/* <TableHead className="whitespace-nowrap text-right text-xs min-w-[130px]">Pending to Vendor</TableHead> */}
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[90px]">Proc Fees (%)</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[110px]">Proc Fees (₹)</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[100px]">Interim Rent</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[120px]">Upfront Revenue</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[120px]">Unsold Rental Revenue</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[80px]">PTPM</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[100px]">Base Rental</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[80px]">GST</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[110px]">GST over Tenure</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[100px]">Actual GST to be Paid</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[90px]">Refund SD (%)</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[100px]">Refund SD (₹)</TableHead>
                      <TableHead className="whitespace-nowrap text-xs min-w-[110px]">Refund SD Date</TableHead>
                      <TableHead className="whitespace-nowrap text-xs min-w-[110px]">Rental Start</TableHead>
                      <TableHead className="whitespace-nowrap text-xs min-w-[110px]">Rental Matured</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[110px]">To Astrafin</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[100px]">To Lender</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[120px]">Remit by Astrafin</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[120px]">Number of Rentals Sold</TableHead>
                      <TableHead className="whitespace-nowrap text-center text-xs min-w-[80px]">Status</TableHead>
                      <TableHead className="whitespace-nowrap text-center text-xs min-w-[60px]">View</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={35} className="text-center py-8 text-muted-foreground">
                          Loading revenue MIS...
                        </TableCell>
                      </TableRow>
                    ) : revenueData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={35} className="text-center py-8 text-muted-foreground">
                          No revenue data available. Create a rental schedule to see revenue MIS.
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {revenueData.map((e, i) => (
                          <TableRow key={i} className="hover:bg-accent/5 text-xs">
                            <TableCell className={`sticky ${COL1_LEFT} z-10 bg-white whitespace-nowrap border-r ${COL1_W}`}>{e.disbursementDate}</TableCell>
                            <TableCell className={`sticky ${COL2_LEFT} z-10 bg-white whitespace-nowrap border-r ${COL2_W}`}>
                              {isMissingLenderName(e.lenderName) ? (
                                <LenderAvatar name="Astrafin" logoUrl={astrafinLogo} />
                              ) : (
                                <LenderAvatar
                                  name={e.lenderName}
                                  logoUrl={toAbsoluteLenderAvatarUrl((e as any).lenderAvatar)}
                                />
                              )}
                            </TableCell>
                            <TableCell className={`sticky ${COL3_LEFT} z-10 bg-white whitespace-nowrap border-r font-medium ${COL3_W}`}>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="block truncate">{e.customerName}</span>
                                  </TooltipTrigger>
                                  <TooltipContent>{e.customerName}</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </TableCell>
                            <TableCell className="whitespace-nowrap">{e.clientId}</TableCell>
                            <TableCell className="whitespace-nowrap">{e.rentalScheduleNumber}</TableCell>
                            <TableCell className="whitespace-nowrap">{e.transactionType}</TableCell>
                            <TableCell className="text-right"> { e.frequency === "monthly" ? e.tenure + "M" : e.tenure/3 + "Q" } </TableCell>
                            <TableCell className="text-right">{fmt(e.baseAmount)}</TableCell>
                            <TableCell className="text-right">{fmt(e.gst)}</TableCell>
                            <TableCell className="text-right font-medium">{fmt(e.invoiceTotalValue)}</TableCell>
                            <TableCell className="text-right">{fmt(e.disbursedAmountFromLender)}</TableCell>
                            <TableCell className="text-right">{fmt(e.lenderProcessingFeesINR)}</TableCell>
                            <TableCell className="text-right">{e.securityDepositPercent}%</TableCell>
                            <TableCell className="text-right">{fmt(e.securityDepositINR)}</TableCell>
                            {/* <TableCell className="text-right">{fmt(e.paymentToVendor)}</TableCell> */}
                            {/* <TableCell className="text-right">{e.paymentPendingToVendor > 0 ? <span className="text-amber-600 font-medium">{fmt(e.paymentPendingToVendor)}</span> : fmt(0)}</TableCell> */}
                            <TableCell className="text-right">{e.processingFeePercent}%</TableCell>
                            <TableCell className="text-right">{fmt(e.processingFee)}</TableCell>
                            <TableCell className="text-right">{fmt(e.interimRent)}</TableCell>
                            <TableCell
                              className={cn(
                                "text-right font-medium",
                                upfrontRevenueToneClass(e.upfrontRevenue),
                              )}
                            >
                              {fmt(e.upfrontRevenue)}
                            </TableCell>
                            <TableCell className="text-right">{fmt(getUnsoldRentalRevenue(e))}</TableCell>
                            <TableCell className="text-right">{e.ptpm}</TableCell>
                            <TableCell className="text-right">{fmt(e.baseRental)}</TableCell>
                            <TableCell className="text-right">{e.gstRental}</TableCell>
                            <TableCell className="text-right">{fmt(e.gstOverTenure)}</TableCell>
                            <TableCell className="text-right">{fmt(getActualGstToBePaid(e))}</TableCell>
                            <TableCell className="text-right">{e.refundSDPercent?.toFixed(0) ?? 0}%</TableCell>
                            <TableCell className="text-right">{fmt(e.refundSDINR)}</TableCell>
                            <TableCell className="whitespace-nowrap">{e.refundSDDate}</TableCell>
                            <TableCell className="whitespace-nowrap">{e.rentalStartDate}</TableCell>
                            <TableCell className="whitespace-nowrap">{e.rentalMaturedDate}</TableCell>
                            <TableCell className="text-right font-medium">{fmt(e.overallToAstrafin)}</TableCell>
                            <TableCell className="text-right">{fmt(e.overallToLender)}</TableCell>
                            <TableCell className="text-right">{fmt(e.remitByAstrafin)}</TableCell>
                            <TableCell className="text-right">{ e.frequency === "monthly" ? e.numberOfRentalsSold + "M" : e.numberOfRentalsSold + "Q" }</TableCell>
                            <TableCell className="text-center"><StatusBadge status={getActiveOrMaturedStatus(e as any)} /></TableCell>
                            <TableCell className="text-center">
                              <ViewButton onClick={() => setViewEntry(e)} hasOutstanding={e.paymentPendingToVendor > 0} />
                            </TableCell>
                          </TableRow>
                        ))}
                        {/* Totals row — column order must match data rows above */}
                        <TableRow className="font-semibold border-t-2 text-xs">
                          <TableCell className={`sticky ${COL1_LEFT} z-20 bg-muted whitespace-nowrap border-r ${COL1_W}`}>TOTAL</TableCell>
                          <TableCell className={`sticky ${COL2_LEFT} z-20 bg-muted border-r ${COL2_W}`} />
                          <TableCell className={`sticky ${COL3_LEFT} z-20 bg-muted border-r ${COL3_W}`} />
                          <TableCell className="bg-muted" />
                          <TableCell className="bg-muted" />
                          <TableCell className="bg-muted" />
                          <TableCell className="bg-muted" />
                          <TableCell className="text-right bg-muted">{fmtTotal(tableTotals.totalBaseAssetValue)}</TableCell>
                          <TableCell className="text-right bg-muted">{fmtTotal(tableTotals.totalAssetGst)}</TableCell>
                          <TableCell className="text-right bg-muted">{fmtTotal(tableTotals.totalAssetValue)}</TableCell>
                          <TableCell className="text-right bg-muted">{fmtTotal(tableTotals.totalDisbursedAmount)}</TableCell>
                          <TableCell className="text-right bg-muted">{fmtTotal(tableTotals.totalProcessingFee)}</TableCell>
                          <TableCell className="bg-muted" />
                          <TableCell className="text-right bg-muted">{fmtTotal(tableTotals.totalSecurityDeposit)}</TableCell>
                          <TableCell className="bg-muted" />
                          <TableCell className="bg-muted" />
                          <TableCell className="bg-muted" />
                          <TableCell className="text-right bg-muted">{fmtTotal(tableTotals.totalUpfrontRevenue)}</TableCell>
                          <TableCell className="bg-muted" />
                          <TableCell className="bg-muted" />
                          <TableCell className="text-right bg-muted">{fmtTotal(tableTotals.totalBaseRental)}</TableCell>
                          <TableCell className="text-right bg-muted">{fmtTotal(tableTotals.totalGst)}</TableCell>
                          <TableCell className="text-right bg-muted">{fmtTotal(tableTotals.totalGstOverTenure)}</TableCell>
                          <TableCell className="text-right bg-muted">{fmtTotal(tableTotals.totalActualGstToBePaid)}</TableCell>
                          <TableCell className="bg-muted" />
                          <TableCell className="text-right bg-muted">{fmtTotal(tableTotals.totalRefundSecurityDeposit)}</TableCell>
                          <TableCell className="bg-muted" />
                          <TableCell className="bg-muted" />
                          <TableCell className="bg-muted" />
                          <TableCell className="bg-muted" />
                          <TableCell className="bg-muted" />
                          <TableCell className="bg-muted" />
                          <TableCell className="bg-muted" />
                          <TableCell className="bg-muted" />
                          <TableCell className="bg-muted" />
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </div>
              {disbursementTotalPages > 1 && (
                <PaginationBar current={disbursementPage} total={disbursementTotalPages} count={disbursementTotalCount} onPage={setDisbursementPage} />
              )}
            </TabsContent>

            {/* ── Leasing Summary Tab ── */}
            <TabsContent value="summary" className="mt-0 w-full max-w-full overflow-hidden">
              <div className="w-full max-w-full h-[500px] overflow-x-auto overflow-y-auto overscroll-contain border rounded-md">
                <Table className="w-max min-w-full" style={{ minWidth: "2200px" }}>
                  <TableHeader className="sticky top-0 z-30 bg-muted/95 backdrop-blur-sm">
                    <TableRow>
                      <TableHead className={`sticky ${COL1_LEFT} z-40 bg-muted/95 whitespace-nowrap border-r text-xs ${COL1_W}`}>Client ID</TableHead>
                      <TableHead className={`sticky ${COL2_LEFT} z-40 bg-muted/95 whitespace-nowrap border-r text-xs ${COL2_W}`}>Lender Name</TableHead>
                      <TableHead className={`sticky ${COL3_LEFT} z-40 bg-muted/95 whitespace-nowrap border-r text-xs ${COL3_W}`}>Counterparty Name</TableHead>
                      <TableHead className="whitespace-nowrap text-xs min-w-[110px]">Transaction Type</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[120px]">Sanctioned Limit</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[90px]">Proc Fees (%)</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[110px]">Proc Fees</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[110px]">Partner Payout (%)</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[110px]">Partner Payout (₹)</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[120px]">Total Invoice Value</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[110px]">Utilized Limit</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[110px]">Unutilized Limit</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[110px]">Potential Invoice</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[120px]">Upfront Revenue</TableHead>
                      {/* <TableHead className="whitespace-nowrap text-right text-xs min-w-[120px]">Unsold Rental Revenue</TableHead> */}
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[130px]">Total Upfront Revenue</TableHead>
                      <TableHead className="whitespace-nowrap text-center text-xs min-w-[60px]">View</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={16} className="text-center py-8 text-muted-foreground">
                          Loading summary MIS...
                        </TableCell>
                      </TableRow>
                    ) : summaryData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={16} className="text-center py-8 text-muted-foreground">
                          No summary data available.
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {summaryData.map((e, i) => (
                          <TableRow key={i} className="text-xs">
                            <TableCell className={`sticky ${COL1_LEFT} z-10 bg-white whitespace-nowrap border-r ${COL1_W}`}>{e.clientId}</TableCell>
                            <TableCell className={`sticky ${COL2_LEFT} z-10 bg-white whitespace-nowrap border-r ${COL2_W}`}>
                              {isMissingLenderName(e.lenderName) ? (
                                <LenderAvatar name="Astrafin" logoUrl={astrafinLogo} />
                              ) : (
                                <LenderAvatar
                                  name={e.lenderName}
                                  logoUrl={toAbsoluteLenderAvatarUrl((e as any).lenderAvatar)}
                                />
                              )}
                            </TableCell>
                            <TableCell className={`sticky ${COL3_LEFT} z-10 bg-white whitespace-nowrap border-r font-medium ${COL3_W}`}>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="block truncate">{e.counterpartyName}</span>
                                  </TooltipTrigger>
                                  <TooltipContent>{e.counterpartyName}</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </TableCell>
                            <TableCell className="whitespace-nowrap">{e.transactionType}</TableCell>
                            <TableCell className="text-right">{fmt(e.sanctionedLimit)}</TableCell>
                            <TableCell className="text-right">{e.processingFeesPercent.toFixed(2)}%</TableCell>
                            <TableCell className="text-right">{fmt(e.processingFees)}</TableCell>
                            <TableCell className="text-right">{e.partnerPayoutPercent.toFixed(2)}%</TableCell>
                            <TableCell className="text-right">{fmt(e.partnerPayoutINR)}</TableCell>
                            <TableCell className="text-right">{fmt(e.totalInvoiceValue)}</TableCell>
                            <TableCell className="text-right">{fmt(e.utilizedLimit)}</TableCell>
                            <TableCell className="text-right">{fmt(e.unutilizedLimit)}</TableCell>
                            <TableCell className="text-right">{fmt(e.potentialInvoice)}</TableCell>
                            <TableCell
                              className={cn(
                                "text-right font-medium",
                                upfrontRevenueToneClass(e.upfrontRevenue),
                              )}
                            >
                              {fmt(e.upfrontRevenue)}
                            </TableCell>
                            <TableCell className="text-right font-medium">{fmt(e.totalUpfrontRevenue)}</TableCell>
                            {/* <TableCell className="text-right">{fmt(e.baseRental * (e.tenure - e.numberOfRentalsSold))}</TableCell> */}
                            <TableCell className="text-center">
                              <ViewButton onClick={() => setViewSummaryEntry(e)} />
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="font-semibold border-t-2 text-xs">
                          <TableCell className={`sticky ${COL1_LEFT} z-20 bg-muted whitespace-nowrap border-r ${COL1_W}`}>TOTAL</TableCell>
                          <TableCell className={`sticky ${COL2_LEFT} z-20 bg-muted border-r ${COL2_W}`} />
                          <TableCell className={`sticky ${COL3_LEFT} z-20 bg-muted border-r ${COL3_W}`} />
                          <TableCell className="bg-muted" />
                          <TableCell className="text-right bg-muted">{fmt(summaryTotals.sanctionedLimit)}</TableCell>
                          <TableCell className="bg-muted" />
                          <TableCell className="text-right bg-muted">{fmt(summaryTotals.processingFees)}</TableCell>
                          <TableCell className="bg-muted" />
                          <TableCell className="text-right bg-muted">{fmt(summaryTotals.partnerPayoutINR)}</TableCell>
                          <TableCell className="text-right bg-muted">{fmt(summaryTotals.totalInvoiceValue)}</TableCell>
                          <TableCell className="text-right bg-muted">{fmt(summaryTotals.utilizedLimit)}</TableCell>
                          <TableCell className="text-right bg-muted">{fmt(summaryTotals.unutilizedLimit)}</TableCell>
                          <TableCell className="text-right bg-muted">{fmt(summaryTotals.potentialInvoice)}</TableCell>
                          <TableCell className="text-right bg-muted">{fmt(summaryTotals.upfrontRevenue)}</TableCell>
                          <TableCell className="text-right bg-muted">{fmt(summaryTotals.totalUpfrontRevenue)}</TableCell>
                          <TableCell className="bg-muted" />
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </div>
              {summaryTotalPages > 1 && (
                <PaginationBar current={summaryPage} total={summaryTotalPages} count={summaryTotalCount} onPage={setSummaryPage} />
              )}
            </TabsContent>
            {/* ── Hybrid Tab ── */}
            <TabsContent value="hybrid" className="mt-0 w-full max-w-full overflow-hidden">
              <div className="w-full max-w-full h-[500px] overflow-x-auto overflow-y-auto overscroll-contain border rounded-md">
                <Table className="w-max min-w-full" style={{ minWidth: "2400px" }}>
                  <TableHeader className="sticky top-0 z-30 bg-muted/95 backdrop-blur-sm">
                    <TableRow>
                      <TableHead className={`sticky ${COL1_LEFT} z-40 bg-muted/95 whitespace-nowrap border-r text-xs ${COL1_W}`}>Client ID</TableHead>
                      <TableHead className={`sticky ${COL2_LEFT} z-40 bg-muted/95 whitespace-nowrap border-r text-xs ${COL2_W}`}>Customer Name</TableHead>
                      <TableHead className={`sticky ${COL3_LEFT} z-40 bg-muted/95 whitespace-nowrap border-r text-xs ${COL3_W}`}>Lending Partner</TableHead>
                      <TableHead className="whitespace-nowrap text-xs min-w-[110px]">Transaction Type</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[130px]">Requested Loan Amt</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[100px]">Tenure (Months)</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[120px]">Sanctioned Amt</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[120px]">Disbursed Amt</TableHead>
                      <TableHead className="whitespace-nowrap text-xs min-w-[130px]">Disbursement Date</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[120px]">Cust Referral Fee %</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[130px]">Cust Referral Fee Amt</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[120px]">Lender Comm %</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[120px]">Lender Comm Amt</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[130px]">Partner Rev Share %</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[130px]">Partner Rev Share Amt</TableHead>
                      <TableHead className="whitespace-nowrap text-right text-xs min-w-[130px]">Revenue Recognition</TableHead>
                      {/* <TableHead className="whitespace-nowrap text-right text-xs min-w-[110px]">Gross Revenue</TableHead> */}
                      <TableHead className="whitespace-nowrap text-center text-xs min-w-[60px]">View</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={18} className="text-center py-8 text-muted-foreground">
                          Loading hybrid MIS...
                        </TableCell>
                      </TableRow>
                    ) : hybridData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={18} className="text-center py-8 text-muted-foreground">
                          No hybrid data available. Create a rental schedule to see hybrid MIS.
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {hybridData.map((e, i) => (
                          <TableRow key={i} className="text-xs">
                            <TableCell className={`sticky ${COL1_LEFT} z-10 bg-white whitespace-nowrap border-r ${COL1_W}`}>{e.clientId}</TableCell>
                            <TableCell className={`sticky ${COL2_LEFT} z-10 bg-white whitespace-nowrap border-r font-medium ${COL2_W}`}>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="block truncate">{e.customerName}</span>
                                  </TooltipTrigger>
                                  <TooltipContent>{e.customerName}</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </TableCell>
                            <TableCell className={`sticky ${COL3_LEFT} z-10 bg-white whitespace-nowrap border-r ${COL3_W}`}>
                              <LenderAvatar name={e.lendingPartner} logoUrl={toAbsoluteLenderAvatarUrl(e.lenderAvatar as string)} />
                            </TableCell>
                            <TableCell className="whitespace-nowrap">{e.transactionType}</TableCell>
                            <TableCell className="text-right">{fmt(e.requestedLoanAmount)}</TableCell>
                            <TableCell className="text-right">{e.tenureMonths} mo</TableCell>
                            <TableCell className="text-right">{fmt(e.sanctionedAmount)}</TableCell>
                            <TableCell className="text-right">{fmt(e.disbursedAmount)}</TableCell>
                            <TableCell className="whitespace-nowrap">{e.disbursementDate}</TableCell>
                            <TableCell className="text-right">{e.customerReferralFeePercent.toFixed(2)}%</TableCell>
                            <TableCell className="text-right">{fmt(e.customerReferralFeeAmount)}</TableCell>
                            <TableCell className="text-right">{e.lenderCommissionPercent.toFixed(2)}%</TableCell>
                            <TableCell className="text-right">{fmt(e.lenderCommissionAmount)}</TableCell>
                            <TableCell className="text-right">{e.partnerRevenueSharePercent.toFixed(2)}%</TableCell>
                            <TableCell className="text-right">{fmt(e.partnerRevenueShareAmount)}</TableCell>
                            <TableCell className="text-right font-medium text-emerald-600">{fmt(e.lenderCommissionAmount + e.customerReferralFeeAmount - e.partnerRevenueShareAmount)}</TableCell>
                            {/* <TableCell className="text-right font-medium">{fmt(e.grossRevenue)}</TableCell> */}
                            <TableCell className="text-center">
                              <ViewButton onClick={() => setViewHybridEntry(e)} />
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="font-semibold border-t-2 text-xs">
                          <TableCell className={`sticky ${COL1_LEFT} z-20 bg-muted whitespace-nowrap border-r ${COL1_W}`}>TOTAL</TableCell>
                          <TableCell className={`sticky ${COL2_LEFT} z-20 bg-muted border-r ${COL2_W}`} />
                          <TableCell className={`sticky ${COL3_LEFT} z-20 bg-muted border-r ${COL3_W}`} />
                          <TableCell className="bg-muted" />
                          <TableCell className="text-right bg-muted">{fmt(hybridTotals.requestedLoanAmount)}</TableCell>
                          <TableCell className="bg-muted" />
                          <TableCell className="text-right bg-muted">{fmt(hybridTotals.sanctionedAmount)}</TableCell>
                          <TableCell className="text-right bg-muted">{fmt(hybridTotals.disbursedAmount)}</TableCell>
                          <TableCell className="bg-muted" />
                          <TableCell className="bg-muted" />
                          <TableCell className="text-right bg-muted">{fmt(hybridTotals.customerReferralFeeAmount)}</TableCell>
                          <TableCell className="bg-muted" />
                          <TableCell className="text-right bg-muted">{fmt(hybridTotals.lenderCommissionAmount)}</TableCell>
                          <TableCell className="bg-muted" />
                          <TableCell className="text-right bg-muted">{fmt(hybridTotals.partnerRevenueShareAmount)}</TableCell>
                          <TableCell className="text-right bg-muted">{fmt(hybridTotals.lenderCommissionAmount + hybridTotals.customerReferralFeeAmount - hybridTotals.partnerRevenueShareAmount)}</TableCell>
                          {/* <TableCell className="text-right bg-muted">{fmt(hybridTotals.grossRevenue)}</TableCell> */}
                          <TableCell className="bg-muted" />
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </div>
              {hybridTotalPages > 1 && (
                <PaginationBar current={hybridPage} total={hybridTotalPages} count={hybridTotalCount} onPage={setHybridPage} />
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* ── Disbursement Detail Sheet ── */}
      <Sheet open={!!viewEntry} onOpenChange={(open) => !open && setViewEntry(null)}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-lg">Disbursement Details</SheetTitle>
          </SheetHeader>
          {viewEntry && (
            <div className="mt-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Customer</p>
                  <p className="font-semibold">{viewEntry.customerName}</p>
                </div>
                <StatusBadge status={getActiveOrMaturedStatus(viewEntry as any)} />
              </div>

              <DetailRow icon={<Landmark className="h-3.5 w-3.5" />} label="Lender" value={viewEntry.lenderName} />
              <DetailRow icon={<CreditCard className="h-3.5 w-3.5" />} label="Client ID" value={viewEntry.clientId} />
              <DetailRow label="Transaction Type" value={viewEntry.transactionType} />
              <DetailRow label="Tenure" value={`${viewEntry.frequency === "monthly" ? viewEntry.tenure + "M" : viewEntry.tenure/3 + "Q"}`} />
              <DetailRow label="Number of Rentals Sold" value={`${viewEntry.frequency === "monthly" ? viewEntry.numberOfRentalsSold + "M" : viewEntry.numberOfRentalsSold + "Q"}`} />
              <DetailRow icon={<Calendar className="h-3.5 w-3.5" />} label="Disbursement Date" value={viewEntry.disbursementDate || "—"} />
              <DetailRow label="Rental Start" value={viewEntry.rentalStartDate || "—"} />
              <DetailRow label="Rental Matured" value={viewEntry.rentalMaturedDate || "—"} />

              <h4 className="font-semibold text-sm pt-2 flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" /> Asset & Disbursement
              </h4>
              <DetailRow label="Asset Base Amount" value={fmt(viewEntry.baseAmount)} />
              <DetailRow label="GST" value={fmt(viewEntry.gst)} />
              <DetailRow label="Total Asset Value" value={fmt(viewEntry.invoiceTotalValue)} />
              <DetailRow label="Disbursed from Lender" value={fmt(viewEntry.disbursedAmountFromLender)} />
              <DetailRow icon={<ShieldCheck className="h-3.5 w-3.5" />} label={`Security Deposit (${viewEntry.securityDepositPercent.toFixed(1)}%)`} value={fmt(viewEntry.securityDepositINR)} />
              {/* <DetailRow label="Payment to Vendor" value={fmt(viewEntry.paymentToVendor)} /> */}
              {/* <DetailRow label="Payment Pending to Vendor" value={fmt(viewEntry.paymentPendingToVendor)} highlight={viewEntry.paymentPendingToVendor > 0} /> */}

              <h4 className="font-semibold text-sm pt-2 flex items-center gap-2">
                <Receipt className="h-4 w-4 text-muted-foreground" /> Revenue & Rentals
              </h4>
              <DetailRow label={`Processing Fees (${viewEntry.processingFeePercent.toFixed(1)}%)`} value={fmt(viewEntry.processingFee)} />
              <DetailRow label="Interim Rent" value={fmt(viewEntry.interimRent)} />
              <DetailRow
                label="Upfront Revenue"
                value={fmt(viewEntry.upfrontRevenue)}
                valueClassName={upfrontRevenueToneClass(viewEntry.upfrontRevenue)}
              />
              <DetailRow label="Unsold Rental Revenue" value={fmt(getUnsoldRentalRevenue(viewEntry))} />
              <DetailRow label="PTPM" value={String(viewEntry.ptpm)} />
              <DetailRow label="Base Rental" value={fmt(viewEntry.baseRental)} />
              <DetailRow label="GST on Rental" value={fmt(viewEntry.gstRental)} />
              <DetailRow label="GST over Tenure" value={fmt(viewEntry.gstOverTenure)} />
              <DetailRow label="Actual GST to be Paid" value={fmt(getActualGstToBePaid(viewEntry))} />

              <h4 className="font-semibold text-sm pt-2 flex items-center gap-2">
                <RefreshCcw className="h-4 w-4 text-muted-foreground" /> Security Deposit Refund
              </h4>
              <DetailRow label={`Refund SD (${viewEntry.refundSDPercent.toFixed(0)}%)`} value={fmt(viewEntry.refundSDINR)} />
              <DetailRow label="Refund SD Date" value={viewEntry.refundSDDate || "—"} />

              <h4 className="font-semibold text-sm pt-2 flex items-center gap-2">
                <ArrowRightLeft className="h-4 w-4 text-muted-foreground" /> Fund Flow Summary
              </h4>
              <DetailRow label="Overall To Astrafin" value={fmt(viewEntry.overallToAstrafin)} />
              <DetailRow label="Overall To Lender" value={fmt(viewEntry.overallToLender)} />
              <DetailRow label="Remit by Astrafin" value={fmt(viewEntry.remitByAstrafin)} />
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* ── Summary Detail Sheet ── */}
      <Sheet open={!!viewSummaryEntry} onOpenChange={(open) => !open && setViewSummaryEntry(null)}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-lg">Leasing Summary Details</SheetTitle>
          </SheetHeader>
          {viewSummaryEntry && (
            <div className="mt-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Counterparty</p>
                  <p className="font-semibold">{viewSummaryEntry.counterpartyName}</p>
                </div>
                <LenderAvatar name={viewSummaryEntry.lenderName} />
              </div>

              <DetailRow icon={<CreditCard className="h-3.5 w-3.5" />} label="Client ID" value={viewSummaryEntry.clientId} />
              <DetailRow label="Transaction Type" value={viewSummaryEntry.transactionType} />

              <h4 className="font-semibold text-sm pt-2 flex items-center gap-2">
                <Landmark className="h-4 w-4 text-muted-foreground" /> Limits & Utilization
              </h4>
              <DetailRow label="Sanctioned Limit" value={fmt(viewSummaryEntry.sanctionedLimit)} />
              <DetailRow label="Total Invoice Value" value={fmt(viewSummaryEntry.totalInvoiceValue)} />
              <DetailRow label="Utilized Limit" value={fmt(viewSummaryEntry.utilizedLimit)} />
              <DetailRow label="Unutilized Limit" value={fmt(viewSummaryEntry.unutilizedLimit)} />
              <DetailRow label="Potential Invoice" value={fmt(viewSummaryEntry.potentialInvoice)} />

              <h4 className="font-semibold text-sm pt-2 flex items-center gap-2">
                <Receipt className="h-4 w-4 text-muted-foreground" /> Fees & Revenue
              </h4>
              <DetailRow label={`Processing Fees (${viewSummaryEntry.processingFeesPercent.toFixed(2)}%)`} value={fmt(viewSummaryEntry.processingFees)} />
              <DetailRow label={`Partner Payout (${viewSummaryEntry.partnerPayoutPercent.toFixed(2)}%)`} value={fmt(viewSummaryEntry.partnerPayoutINR)} />
              <DetailRow label="Upfront Revenue" value={fmt(viewSummaryEntry.upfrontRevenue)} />
              <DetailRow label="Total Upfront Revenue" value={fmt(viewSummaryEntry.totalUpfrontRevenue)} />
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* ── Hybrid Detail Sheet ── */}
      <Sheet open={!!viewHybridEntry} onOpenChange={(open) => !open && setViewHybridEntry(null)}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-lg">Hybrid Details</SheetTitle>
          </SheetHeader>
          {viewHybridEntry && (
            <div className="mt-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Customer</p>
                  <p className="font-semibold">{viewHybridEntry.customerName}</p>
                </div>
                <LenderAvatar name={viewHybridEntry.lendingPartner} />
              </div>

              <DetailRow icon={<CreditCard className="h-3.5 w-3.5" />} label="Client ID" value={viewHybridEntry.clientId} />
              <DetailRow label="Transaction Type" value={viewHybridEntry.transactionType} />
              <DetailRow icon={<Calendar className="h-3.5 w-3.5" />} label="Disbursement Date" value={viewHybridEntry.disbursementDate || "—"} />

              <h4 className="font-semibold text-sm pt-2 flex items-center gap-2">
                <Banknote className="h-4 w-4 text-muted-foreground" /> Loan & Disbursement
              </h4>
              <DetailRow label="Requested Loan Amount" value={fmt(viewHybridEntry.requestedLoanAmount)} />
              <DetailRow label="Tenure" value={`${viewHybridEntry.tenureMonths} months`} />
              <DetailRow label="Sanctioned Amount" value={fmt(viewHybridEntry.sanctionedAmount)} />
              <DetailRow label="Disbursed Amount" value={fmt(viewHybridEntry.disbursedAmount)} />

              <h4 className="font-semibold text-sm pt-2 flex items-center gap-2">
                <Receipt className="h-4 w-4 text-muted-foreground" /> Commissions & Revenue
              </h4>
              <DetailRow label={`Customer Referral Fee (${viewHybridEntry.customerReferralFeePercent.toFixed(2)}%)`} value={fmt(viewHybridEntry.customerReferralFeeAmount)} />
              <DetailRow label={`Lender Commission (${viewHybridEntry.lenderCommissionPercent.toFixed(2)}%)`} value={fmt(viewHybridEntry.lenderCommissionAmount)} />
              <DetailRow label={`Partner Revenue Share (${viewHybridEntry.partnerRevenueSharePercent.toFixed(2)}%)`} value={fmt(viewHybridEntry.partnerRevenueShareAmount)} />

              <h4 className="font-semibold text-sm pt-2 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-muted-foreground" /> Revenue Summary
              </h4>
              <DetailRow label="Revenue Recognition" value={fmt(viewHybridEntry.revenueRecognition)} />
              {/* <DetailRow label="Gross Revenue" value={fmt(viewHybridEntry.grossRevenue)} /> */}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default RevenueMIS;
